/* eslint-disable no-console */
import {
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
} from '@ant-design/icons';
import { Button, Col, Form, Input, Modal, Row } from 'antd';
import { cloneElement, useState } from 'react';

import { getCurrentBrowserName } from '../../../../helpers/getCurrentBrowserName';
import { useAuth } from '../../../../hooks/useAuth';
import { useMobileVersion } from '../../../../hooks/useMobileVersion';
import { sendFeedback } from '../../../../operations/file';
import {
  FeedbackMetaInfo,
  ObligationFile,
  SendFeedbackParams,
} from '../../../../operations/file/types';
import { TitleStyled } from './styles';

type FeedbackRating = 0 | 1;

interface FeedbackModalProps {
  isVisible: boolean;
  onClose: () => void;
  file?: ObligationFile | null;
  title: string;
}

export const FeedbackModal = ({
  isVisible,
  onClose,
  file,
  title,
}: FeedbackModalProps) => {
  const { user } = useAuth();
  const isMobile = useMobileVersion();

  const [isLoading, setIsLoading] = useState(false);
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [feedbackRating, setFeedbackRating] = useState<FeedbackRating | null>(
    null
  );
  const [isDescriptionErrorVisible, setIsDescriptionErrorVisible] =
    useState(false);

  const isNegativeRating = feedbackRating === 0;

  function clearDescriptionError() {
    if (isDescriptionErrorVisible) {
      setIsDescriptionErrorVisible(false);
    }
  }

  async function handleSendFeedback() {
    if (!user?.id || feedbackRating === null) return;
    if (isNegativeRating && feedbackDescription.length < 50) {
      setIsDescriptionErrorVisible(true);
      return;
    }

    setIsLoading(true);

    try {
      const metaInfo: FeedbackMetaInfo = {
        browser: getCurrentBrowserName(),
        screen: {
          width: window.screen.width,
        },
      };

      if (file) {
        metaInfo.file = {
          id: file.id,
          description: file.description,
        };
      }

      const body: SendFeedbackParams = {
        appUserId: user.id,
        rating: feedbackRating,
        description: feedbackDescription,
        metaInfo,
      };

      await sendFeedback(body);
      setIsFeedbackSent(true);
      clearDescriptionError();
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  function closeModal() {
    setIsFeedbackSent(false);
    setFeedbackRating(null);
    setFeedbackDescription('');
    setIsDescriptionErrorVisible(false);
    setIsLoading(false);
    onClose();
  }

  return (
    <Modal
      width={isMobile ? 'calc(100vw - 32px)' : 400}
      open={isVisible}
      onCancel={closeModal}
      footer={false}
      destroyOnClose
      centered
    >
      {isFeedbackSent ? (
        <>
          <TitleStyled level={4}>Obrigado pelo seu feedback!</TitleStyled>
        </>
      ) : (
        <>
          <TitleStyled level={4}>{title}</TitleStyled>

          <Form layout="vertical">
            <Row>
              <Col span={12}>
                <Form.Item label="Satisfatória">
                  {cloneElement(
                    feedbackRating === 1 ? <LikeFilled /> : <LikeOutlined />,
                    {
                      style: { fontSize: 24, cursor: 'pointer' },
                      onClick: () => {
                        clearDescriptionError();
                        setFeedbackRating(1);
                      },
                    }
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Insatisfatória">
                  {cloneElement(
                    feedbackRating === 0 ? (
                      <DislikeFilled />
                    ) : (
                      <DislikeOutlined />
                    ),
                    {
                      style: { fontSize: 24, cursor: 'pointer' },
                      onClick: () => {
                        clearDescriptionError();
                        setFeedbackRating(0);
                      },
                    }
                  )}
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Conte mais sobre a sua avaliação"
              required={isNegativeRating}
              validateStatus={isDescriptionErrorVisible ? 'error' : undefined}
              help={
                isDescriptionErrorVisible
                  ? `Por favor, informe a descrição da sua avaliação com pelo menos 50 caracteres. (${feedbackDescription.length} no momento).`
                  : undefined
              }
            >
              <Input.TextArea
                value={feedbackDescription}
                onChange={({ target }) => {
                  clearDescriptionError();
                  setFeedbackDescription(target.value);
                }}
              />
            </Form.Item>

            <Button
              type="primary"
              block
              disabled={feedbackRating === null}
              onClick={handleSendFeedback}
              loading={isLoading}
            >
              Enviar
            </Button>
          </Form>
        </>
      )}
    </Modal>
  );
};
